import { useTranslation } from 'next-i18next';
import React, { useCallback, useMemo } from 'react';
import { useSortingOptions } from 'src/features/common/explore/hooks/use-sorting-options';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { SvgIcon } from 'src/ui/common/atoms/svg-icon';
import { Button } from 'src/ui/white-labels/techcontemporary/atoms/button';
import {
    useClearItems,
    useFetchExploreArts,
    useFilters,
    useSetFilters,
    useSetSorting,
    useSorting,
} from '../../hooks/use-explore-arts';
import { TechExploreFiltersModal } from '../../modals/explore-filters-modal';
import { TechExploreSortingModal } from '../../modals/explore-sorting-modal';
import { DesktopActions, MobileActions, StyledSearchInput, StyledSelect, StyledWrapper } from './styles';

const SELECT_OPTIONS = { rootMargin: '-56px 0px 0px 0px' };

export const ExploreTopFiltersContainer = () => {
    const { t } = useTranslation(['components']);
    const sortingOptions = useSortingOptions();
    const setSorting = useSetSorting();
    const sorting = useSorting();
    const clearItems = useClearItems();
    const fetchExploreArts = useFetchExploreArts();
    const filters = useFilters();
    const setFilters = useSetFilters();

    const selectedValue = useMemo(() => sortingOptions.find((el) => el.value === sorting), [sorting, sortingOptions]);

    const locale = useMemo(
        () => ({
            filters: t('misc.filters', { ns: 'actions' }),
            sort: t('exploreFilter.actions.sorting.label', { ns: 'components' }),
        }),
        [t]
    );

    const openFiltersModal = useCallback(() => {
        TechExploreFiltersModal.open({});
    }, []);

    const handleSortChange = useCallback(
        async (option) => {
            if (sorting !== option.value) {
                setSorting(option.value);
                clearItems();
                await fetchExploreArts();
            }
        },
        [clearItems, sorting, setSorting, fetchExploreArts]
    );

    const openSortingModal = useCallback(() => {
        TechExploreSortingModal.open({ onApply: handleSortChange, sorting, sortingOptions });
    }, [handleSortChange, sorting, sortingOptions]);

    return (
        <StyledWrapper>
            <StyledSearchInput
                placeholder={t('explore.searchPlaceholder', { ns: 'tech-contemporary' })}
                filters={filters}
                setFilters={setFilters}
            />
            <MobileActions view="mobile" display="flex">
                <Button variant="tonal" onClick={openSortingModal}>
                    <FlexBox justifyContent="center" alignItems="center" gap="8">
                        <SvgIcon width={20} height={20} variant="regular" color="gs100" icon="filter" />
                        {locale.sort}
                    </FlexBox>
                </Button>
                <Button variant="tonal" onClick={openFiltersModal}>
                    <FlexBox justifyContent="center" alignItems="center" gap="8">
                        <SvgIcon width={20} height={15} variant="regular" color="gs100" icon="tech-filter" />
                        {locale.filters}
                    </FlexBox>
                </Button>
            </MobileActions>
            <DesktopActions view={['laptop', 'desktop']} display="flex">
                <StyledSelect
                    value={selectedValue}
                    options={sortingOptions}
                    inViewOptions={SELECT_OPTIONS}
                    onChange={handleSortChange}
                />
            </DesktopActions>
        </StyledWrapper>
    );
};